import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PaperPlaneTilt, CheckCircle, Eye, WarningCircle, Pulse, ArrowClockwise, CaretDown, CaretRight, Stethoscope, ClockCountdown } from "@phosphor-icons/react";

export default function Messages() {
  const [tab, setTab] = useState("history"); // history | diagnostics
  return (
    <div data-testid="messages-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-2">Delivery Center</div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Messages</h1>
        <p className="text-brand-text/70 mt-1">Every campaign you've sent — plus a diagnostics view so you know exactly why any message didn't land.</p>
      </div>

      <div className="flex gap-2 border-b border-brand-border mb-6">
        <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">
          <PaperPlaneTilt size={16} weight="duotone" /> History
        </TabBtn>
        <TabBtn active={tab === "diagnostics"} onClick={() => setTab("diagnostics")} testid="tab-diagnostics">
          <Stethoscope size={16} weight="duotone" /> Diagnostics
        </TabBtn>
      </div>

      {tab === "history" ? <HistoryTab /> : <DiagnosticsTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${active ? "text-brand-primary border-brand-primary" : "text-brand-text/60 border-transparent hover:text-brand-text"}`}
    >
      {children}
    </button>
  );
}

function HistoryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [syncing, setSyncing] = useState({});

  const load = () => api.get("/messages").then(r => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const sync = async (id) => {
    setSyncing(s => ({ ...s, [id]: true }));
    try { await api.post(`/messages/${id}/sync-status`); await load(); }
    finally { setSyncing(s => ({ ...s, [id]: false })); }
  };

  if (loading) return <div className="text-brand-textMuted py-16 text-center">Loading…</div>;
  if (items.length === 0) return (
    <div className="bg-white border border-brand-border rounded-2xl p-16 text-center" data-testid="msg-empty">
      <PaperPlaneTilt size={40} weight="duotone" className="text-brand-primary mx-auto" />
      <h3 className="font-heading text-xl font-bold mt-4">No messages yet</h3>
      <p className="text-brand-text/70 mt-2">Head to Customers, pick a few, and send your first WhatsApp campaign.</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {items.map(m => {
        const dRate = m.recipients_count ? Math.round((m.delivered_count / m.recipients_count) * 100) : 0;
        const rRate = m.delivered_count ? Math.round((m.read_count / m.delivered_count) * 100) : 0;
        const isOpen = !!expanded[m.id];
        return (
          <div key={m.id} className="bg-white border border-brand-border rounded-2xl p-5 md:p-6" data-testid={`msg-${m.id}`}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-textMuted">{new Date(m.sent_at).toLocaleString()}</div>
                <div className="mt-2 text-brand-text whitespace-pre-wrap">{m.content}</div>
              </div>
              <div className="flex md:flex-col gap-4 md:min-w-[200px]">
                <Metric label="Sent" value={m.recipients_count} />
                <Metric label="Delivered" value={`${m.delivered_count} · ${dRate}%`} icon={CheckCircle} />
                <Metric label="Read" value={`${m.read_count} · ${rRate}%`} icon={Eye} />
                {m.failed_count > 0 && <Metric label="Failed" value={m.failed_count} icon={WarningCircle} tone="danger" />}
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 border-t border-brand-border pt-3">
              <button
                data-testid={`msg-toggle-${m.id}`}
                onClick={() => setExpanded(e => ({ ...e, [m.id]: !isOpen }))}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text/70 hover:text-brand-primary"
              >
                {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                {isOpen ? "Hide recipients" : `Show recipients (${m.recipients?.length || 0})`}
              </button>
              <button
                data-testid={`msg-sync-${m.id}`}
                onClick={() => sync(m.id)}
                disabled={syncing[m.id]}
                className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-brand-primary hover:underline disabled:opacity-50"
              >
                <ArrowClockwise size={14} weight={syncing[m.id] ? "bold" : "regular"} className={syncing[m.id] ? "animate-spin" : ""} />
                {syncing[m.id] ? "Syncing…" : "Sync from Twilio"}
              </button>
            </div>
            {isOpen && (
              <div className="mt-3 border-t border-brand-border pt-3 overflow-x-auto">
                <table className="w-full text-xs" data-testid={`msg-recipients-${m.id}`}>
                  <thead className="text-brand-textMuted uppercase tracking-wider">
                    <tr>
                      <th className="text-left py-2 pr-2">Recipient</th>
                      <th className="text-left py-2 pr-2">Status</th>
                      <th className="text-left py-2 pr-2">Error</th>
                      <th className="text-left py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border/50">
                    {(m.recipients || []).map((r, i) => (
                      <tr key={i} data-testid={`recipient-${m.id}-${i}`}>
                        <td className="py-2 pr-2 font-mono">{r.phone_masked || "-"}</td>
                        <td className="py-2 pr-2"><StatusPill status={r.status} /></td>
                        <td className="py-2 pr-2 font-mono text-brand-text/60">{r.error_code || "—"}</td>
                        <td className="py-2 text-brand-text/70 max-w-md">{r.error_reason || (r.status === "read" ? "Read by recipient" : r.status === "delivered" ? "Delivered to device" : r.status === "sent" ? "Left Twilio — awaiting Meta ack" : "—")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DiagnosticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/messages/diagnostics").then(r => { setData(r.data); setLoading(false); }); }, []);
  if (loading) return <div className="text-brand-textMuted py-16 text-center">Loading diagnostics…</div>;
  if (!data || data.kpi.total === 0) return (
    <div className="bg-white border border-brand-border rounded-2xl p-16 text-center" data-testid="diag-empty">
      <Stethoscope size={40} weight="duotone" className="text-brand-primary mx-auto" />
      <h3 className="font-heading text-xl font-bold mt-4">No delivery data yet</h3>
      <p className="text-brand-text/70 mt-2">Send your first campaign and this page will light up with delivery health, error codes, and per-recipient reasons.</p>
    </div>
  );

  const { kpi, rates, errors, recent_failures, twilio_mode } = data;
  const badMode = twilio_mode === "sandbox";

  return (
    <div className="space-y-6" data-testid="diagnostics-content">
      {badMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="diag-sandbox-warn">
          <WarningCircle size={20} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <div className="font-bold">You're in Twilio Sandbox mode.</div>
            <div className="mt-1">Recipients must have joined the sandbox (send <code className="px-1 bg-amber-100 rounded">join &lt;keyword&gt;</code>) before your messages arrive. This is the preview environment — production uses your live sender.</div>
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Total sent" value={kpi.total} icon={PaperPlaneTilt} />
        <KpiCard label="Delivery rate" value={`${rates.delivery}%`} icon={CheckCircle} tone={rates.delivery >= 80 ? "good" : rates.delivery >= 50 ? "warn" : "danger"} />
        <KpiCard label="Read rate" value={`${rates.read}%`} icon={Eye} tone="neutral" />
        <KpiCard label="Silent drops" value={kpi.silent_drops} sub={`${rates.silent}% of total`} icon={ClockCountdown} tone={kpi.silent_drops > 0 ? "warn" : "neutral"} />
        <KpiCard label="Failed" value={kpi.failed + kpi.undelivered} sub={`${rates.failure}% of total`} icon={WarningCircle} tone={(kpi.failed + kpi.undelivered) > 0 ? "danger" : "neutral"} />
      </div>

      {/* Error breakdown */}
      <div className="bg-white border border-brand-border rounded-2xl p-5 md:p-6" data-testid="diag-errors-card">
        <div className="flex items-center gap-2 mb-4">
          <Pulse size={20} weight="duotone" className="text-brand-primary" />
          <h3 className="font-heading font-bold text-lg">Error breakdown</h3>
        </div>
        {errors.length === 0 ? (
          <p className="text-sm text-brand-text/60">No errors recorded. Every send left Twilio cleanly.</p>
        ) : (
          <div className="space-y-2">
            {errors.map(e => (
              <div key={e.code} className="border border-brand-border rounded-lg p-3 flex items-start gap-3" data-testid={`err-${e.code}`}>
                <div className="min-w-[70px] px-2 py-1 bg-brand-primary/10 rounded font-mono text-xs font-bold text-brand-primary text-center">{e.code}</div>
                <div className="flex-1">
                  <div className="text-sm text-brand-text">{e.reason || "Unknown reason"}</div>
                  <div className="text-xs text-brand-text/50 mt-0.5">Occurred {e.count} {e.count === 1 ? "time" : "times"}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent failures */}
      <div className="bg-white border border-brand-border rounded-2xl p-5 md:p-6" data-testid="diag-recent-card">
        <div className="flex items-center gap-2 mb-4">
          <WarningCircle size={20} weight="duotone" className="text-red-500" />
          <h3 className="font-heading font-bold text-lg">Recent failures</h3>
        </div>
        {recent_failures.length === 0 ? (
          <p className="text-sm text-brand-text/60">Nothing to worry about — no failed deliveries in the last 500 messages.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-brand-textMuted uppercase tracking-wider">
                <tr>
                  <th className="text-left py-2 pr-2">When</th>
                  <th className="text-left py-2 pr-2">Recipient</th>
                  <th className="text-left py-2 pr-2">Status</th>
                  <th className="text-left py-2 pr-2">Code</th>
                  <th className="text-left py-2">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border/50">
                {recent_failures.map((f, i) => (
                  <tr key={i}>
                    <td className="py-2 pr-2 whitespace-nowrap text-brand-text/60">{new Date(f.sent_at).toLocaleString()}</td>
                    <td className="py-2 pr-2 font-mono">{f.phone_masked}</td>
                    <td className="py-2 pr-2"><StatusPill status={f.status} /></td>
                    <td className="py-2 pr-2 font-mono text-brand-text/60">{f.error_code || "—"}</td>
                    <td className="py-2 text-brand-text/70">{f.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, tone = "neutral" }) {
  const tones = {
    good: "text-emerald-600 bg-emerald-50",
    warn: "text-amber-600 bg-amber-50",
    danger: "text-red-600 bg-red-50",
    neutral: "text-brand-primary bg-brand-primary/5",
  };
  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4">
      <div className="flex items-center gap-2">
        {Icon && <div className={`p-1.5 rounded-lg ${tones[tone]}`}><Icon size={14} weight="duotone" /></div>}
        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-textMuted">{label}</div>
      </div>
      <div className="font-heading font-bold text-2xl mt-2">{value}</div>
      {sub && <div className="text-xs text-brand-text/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const map = {
    read: "bg-emerald-100 text-emerald-700",
    delivered: "bg-sky-100 text-sky-700",
    sent: "bg-slate-100 text-slate-700",
    queued: "bg-slate-100 text-slate-700",
    failed: "bg-red-100 text-red-700",
    undelivered: "bg-red-100 text-red-700",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${map[s] || "bg-slate-100 text-slate-600"}`}>{s || "—"}</span>;
}

function Metric({ label, value, icon: Icon, tone }) {
  return (
    <div className="flex items-center gap-2">
      {Icon && <Icon size={16} weight="duotone" className={tone === "danger" ? "text-red-500" : "text-brand-secondary"} />}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-textMuted">{label}</div>
        <div className={`font-heading font-bold text-sm ${tone === "danger" ? "text-red-600" : ""}`}>{value}</div>
      </div>
    </div>
  );
}
